/**
 * cityNormalizer.js
 *
 * JavaScript mirror of backend/app/utils/city_normalizer.py.
 * Keeps city name handling in sync: accent variants, "City of X" / "X City" suffixes,
 * and canonical display names are resolved identically on both sides of the stack.
 *
 * Public API
 * ----------
 * normalizeCityForComparison(city) → string   lowercase, accent-folded, stripped
 * canonicalCityName(city)          → string   display name (e.g. "Las Piñas")
 * citiesMatch(a, b)                → boolean  true when two city strings refer to the same place
 */

/** Normalized key → canonical display name (mirrors CANONICAL_CITY_NAMES in city_normalizer.py). */
const CANONICAL_CITY_NAMES = {
  paranaque:                'Parañaque',
  'las pinas':              'Las Piñas',
  dasmarinas:               'Dasmariñas',
  'quezon city':            'Quezon City',
  manila:                   'Manila',
  makati:                   'Makati',
  pasig:                    'Pasig',
  taguig:                   'Taguig',
  pasay:                    'Pasay',
  caloocan:                 'Caloocan',
  marikina:                 'Marikina',
  muntinlupa:               'Muntinlupa',
  valenzuela:               'Valenzuela',
  'san juan':               'San Juan',
  bacoor:                   'Bacoor',
  imus:                     'Imus',
  mandaluyong:              'Mandaluyong',
  malabon:                  'Malabon',
  navotas:                  'Navotas',
  antipolo:                 'Antipolo',
  'cebu city':              'Cebu City',
  'davao city':             'Davao City',
  binan:                    'Biñan',
  'santa rosa':             'Santa Rosa',
  'sta rosa':               'Santa Rosa',
  cabuyao:                  'Cabuyao',
  'san pedro':              'San Pedro',
  laguna:                   'Laguna',
  batangas:                 'Batangas',
  cavite:                   'Cavite',
  meycauayan:               'Meycauayan',
  malolos:                  'Malolos',
  bulacan:                  'Bulacan',
  pampanga:                 'Pampanga',
  'meycauayan city':        'Meycauayan City',
  'malolos city':           'Malolos City',
  'san jose del monte city':'San Jose del Monte City',
  calamba:                  'Calamba City',
  'calamba city':           'Calamba City',
  lipa:                     'Lipa City',
  'lipa city':              'Lipa City',
};

/**
 * Restore ñ in a city name that was round-tripped through the wrong codec.
 *
 * A name encoded as UTF-8 but decoded as Latin-1 arrives with every non-ASCII
 * character expanded into two: "Las Piñas" becomes "Las PiÃ±as", which title-casing
 * downstream turns into "Las Piã±As". Such a name matches no canonical entry, so
 * every city-scoped lookup for it resolves to zero rows. Only ñ occurs in the names
 * this system handles, so repairing that one sequence is enough.
 * Mirrors Python repair_mojibake().
 *
 * @param {string|null|undefined} city
 * @returns {string}
 */
export function repairMojibake(city) {
  if (!city || typeof city !== 'string') return '';
  return city.replace(/Ã±/g, 'ñ').replace(/ã±/g, 'ñ');
}

/**
 * Normalize a city string for equality comparison.
 * Strips whitespace, lowercases, removes "City of " prefix, and folds ñ → n.
 * Mirrors Python normalize_city_for_comparison().
 *
 * @param {string|null|undefined} city
 * @returns {string} normalized key
 */
export function normalizeCityForComparison(city) {
  if (!city || typeof city !== 'string') return '';
  let s = repairMojibake(city).trim().toLowerCase();
  if (s.startsWith('city of ')) s = s.slice(8).trim();
  // Fold accented n variants so "Piñas" and "Pinas" compare equal
  s = s.replace(/[ñń]/g, 'n');
  return s;
}

/**
 * Return the canonical display name for a city.
 * Handles accent variants, "X City" suffix, and "City of X" prefix.
 * Unknown cities are returned title-cased.
 * Mirrors Python canonical_city_name().
 *
 * @param {string|null|undefined} city
 * @returns {string}
 */
export function canonicalCityName(city) {
  if (!city || typeof city !== 'string') return city || '';
  city = repairMojibake(city);
  const key = normalizeCityForComparison(city);
  if (!key) return city.trim();

  // Direct lookup
  if (CANONICAL_CITY_NAMES[key]) return CANONICAL_CITY_NAMES[key];

  // Strip trailing " city" suffix and retry (e.g. "Makati City" → "Makati")
  if (key.endsWith(' city')) {
    const bareKey = key.slice(0, -5);
    if (CANONICAL_CITY_NAMES[bareKey]) return CANONICAL_CITY_NAMES[bareKey];
  }

  // Unknown city — return title-cased original
  return city.trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Returns true when two city strings refer to the same city,
 * regardless of accent or suffix differences.
 * Mirrors Python cities_match().
 *
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @returns {boolean}
 */
export function citiesMatch(a, b) {
  const na = normalizeCityForComparison(a);
  const nb = normalizeCityForComparison(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
}
